import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  MessageSquare, 
  Send, 
  Bot, 
  User, 
  Sparkles, 
  FileText, 
  Clock, 
  DollarSign,
  ArrowRight,
  Minimize2,
  Maximize2
} from 'lucide-react';

const AIAssistant = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      content: "Hi! I'm your FormForgePro AI Legal Assistant. I can help you find the right legal documents, explain complex legal concepts, and guide you through our services. What legal matter can I help you with today?",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickQuestions = [
    "What documents do I need for divorce?",
    "How do I form an LLC?",
    "What's included in estate planning?",
    "Help me with trademark registration",
    "What are your legal analysis services?"
  ];

  const suggestedServices = [
    {
      title: "Uncontested Divorce",
      price: "$99",
      duration: "15-20 minutes",
      description: "Complete divorce paperwork for uncontested cases"
    },
    {
      title: "LLC Formation", 
      price: "$149",
      duration: "25-30 minutes",
      description: "Start your business with proper documentation"
    },
    {
      title: "Simple Will",
      price: "$79",
      duration: "20-25 minutes", 
      description: "Basic will document with standard provisions"
    }
  ];

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Simulate AI response (replace with actual AI integration)
    setTimeout(() => {
      const aiResponse = generateAIResponse(inputMessage);
      const assistantMessage = {
        id: messages.length + 2,
        type: 'assistant',
        content: aiResponse,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      setIsLoading(false);
    }, 1500);
  };

  const generateAIResponse = (question) => {
    const lowerQuestion = question.toLowerCase();
    
    if (lowerQuestion.includes('divorce')) {
      return "For divorce proceedings, you'll typically need:\n\n• Petition for Divorce\n• Financial disclosure statements\n• Property settlement agreement\n• Parenting plan (if children are involved)\n• Final decree\n\nOur Uncontested Divorce package ($99) includes all these documents with state-specific requirements. Would you like me to help you get started?";
    }
    
    if (lowerQuestion.includes('llc') || lowerQuestion.includes('business')) {
      return "To form an LLC, you'll need:\n\n• Articles of Organization\n• Operating Agreement\n• EIN application\n• State-specific filing forms\n• Registered agent designation\n\nOur LLC Formation service ($149) handles all paperwork and filing requirements for all 50 states. The process typically takes 25-30 minutes to complete.";
    }
    
    if (lowerQuestion.includes('will') || lowerQuestion.includes('estate')) {
      return "Estate planning typically includes:\n\n• Last Will & Testament\n• Living Will/Advanced Directive\n• Power of Attorney documents\n• Healthcare proxy\n• Beneficiary designations\n\nOur estate planning packages start at $79 for a simple will. For comprehensive estate planning, we recommend our Professional Legal Analysis service to ensure everything is properly coordinated.";
    }
    
    if (lowerQuestion.includes('trademark')) {
      return "For trademark registration, you'll need:\n\n• Trademark application (USPTO Form)\n• Specimen of use\n• Trademark search report\n• Classification of goods/services\n• Filing fees and documentation\n\nOur Trademark Registration service ($179) includes comprehensive search, application preparation, and filing guidance with ongoing brand monitoring options.";
    }
    
    if (lowerQuestion.includes('review') || lowerQuestion.includes('analysis') || lowerQuestion.includes('legal analysis')) {
      return "Our legal services include:\n\n**Professional Legal Analysis ($149)** - 24-48 hours\n• Error detection and compliance check\n• Improvement suggestions and written report\n\n**Express Legal Consultation ($179)** - 4-8 hours  \n• Same-day priority processing\n• Comprehensive review included\n\n**Priority Legal Support ($99)** - Same day\n• One-on-one support and legal guidance\n• Court contacts and deadline reminders\n\nAll services include professional analysis by qualified legal experts.";
    }
    
    if (lowerQuestion.includes('price') || lowerQuestion.includes('cost')) {
      return "Our pricing is transparent and affordable:\n\n• Most documents: $79-$299\n• Legal analysis: $149-$179\n• Priority legal support: $99\n• Express services available\n\nAll services include state-specific forms, filing instructions, and email support. We've helped customers save over $4M in legal fees compared to traditional attorney costs.";
    }
    
    // Default response
    return `I understand you're asking about ${question}. \n\nI can help you with:\n• Finding the right legal documents\n• Explaining our services and pricing\n• Guiding you through the document preparation process\n• Recommending add-on services like legal analysis\n\nFor specific legal advice, I recommend consulting with a qualified attorney. Would you like me to help you find the right FormForgePro service for your needs?`;
  };

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto shadow-lg">
      <CardHeader className="bg-gradient-to-r from-teal-500 to-teal-600 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-full">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <CardTitle className="text-lg">FormForgePro AI Legal Assistant</CardTitle>
              <p className="text-teal-100 text-sm">Powered by real legal databases</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Badge className="bg-white bg-opacity-20 text-white">
              <Sparkles className="w-3 h-3 mr-1" />
              Live
            </Badge>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setIsMinimized(!isMinimized)}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>

      {!isMinimized && (
        <CardContent className="p-0">
          {/* Chat Messages */}
          <div className="h-96 overflow-y-auto p-4 bg-gray-50">
            {messages.map((message) => (
              <div key={message.id} className={`mb-4 flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-start space-x-2 max-w-xs lg:max-w-md ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <div className={`p-2 rounded-full ${message.type === 'user' ? 'bg-teal-500' : 'bg-gray-300'}`}>
                    {message.type === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-gray-700" />
                    )}
                  </div>
                  <div className={`p-3 rounded-lg ${message.type === 'user' ? 'bg-teal-500 text-white' : 'bg-white text-gray-800'}`}>
                    <p className="text-sm whitespace-pre-line">{message.content}</p>
                    <span className="text-xs opacity-70 mt-1 block">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start mb-4">
                <div className="flex items-start space-x-2">
                  <div className="p-2 rounded-full bg-gray-300">
                    <Bot className="w-4 h-4 text-gray-700" />
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          <div className="p-4 bg-white border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Questions:</h4>
            <div className="flex flex-wrap gap-2 mb-4">
              {quickQuestions.map((question, index) => (
                <Button 
                  key={index}
                  variant="outline" 
                  size="sm"
                  onClick={() => handleQuickQuestion(question)}
                  className="text-xs hover:bg-teal-50 hover:border-teal-300"
                >
                  {question}
                </Button>
              ))}
            </div>
          </div>

          {/* Suggested Services */}
          <div className="p-4 bg-teal-50 border-t border-teal-200">
            <h4 className="text-sm font-medium text-teal-800 mb-3">Popular Services:</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {suggestedServices.map((service, index) => (
                <div key={index} className="bg-white p-3 rounded-lg border border-teal-200 hover:shadow-md transition-shadow">
                  <h5 className="font-medium text-gray-800 text-sm">{service.title}</h5>
                  <p className="text-xs text-gray-600 mb-2">{service.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {service.duration}
                    </span>
                    <span className="flex items-center font-medium text-teal-600">
                      <DollarSign className="w-3 h-3 mr-1" />
                      {service.price}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex space-x-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me about legal documents, pricing, or our services..."
                className="flex-1"
                disabled={isLoading}
              />
              <Button 
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim()}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              AI responses are for informational purposes only. For legal advice, consult a qualified attorney.
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default AIAssistant;